import React, { FC, useState, useMemo } from "react";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "../../../components/button";
import { Checkbox } from "../../../components/checkbox";
import { Icon } from "../../../components/icon";
import { Text } from "../../../components/text";
import { TextArea } from "../../../components/textArea";
import { AuthStore, CommentStore, MeetingStore, UserStore } from "src/stores";
import { theme } from "src/config/theme";
import { User, Comment } from "src/models";
import { CommentEntry } from "src/components/commentEntry";
import { Spacer } from "src/components";
import { ItemType } from "src/types";


interface ICommentViewProps {
  authStore?:    AuthStore;
  commentStore?: CommentStore;
  meetingStore?: MeetingStore;
  userStore?:    UserStore;
}

export const CommentView: FC<ICommentViewProps> = inject("authStore", "commentStore", "userStore", "meetingStore")(observer((props: ICommentViewProps): JSX.Element => {
  
    const { t, i18n } = useTranslation();
    const { authStore, commentStore, userStore, meetingStore } = props;
    const [viewPrivate,    setViewPrivate]    = useState<boolean>(false);
    const [isPrivate,      setIsPrivate]      = useState<boolean>(false);
    const [text,     setText]     = useState<string>("");
    const [reply,    setReply]    = useState<Comment>();
    const [mentions, setMentions] = useState<number[]>([]);
    const textAreaRef       = React.useRef<{ setContent: (text: string) => void }>();
    const currentUser: User = authStore.getCurUser;
    const setReplyComment = React.useCallback((comment: Comment) => setReply(comment), []);

    const createComment = async () => {
      const mentionStrings: string[] = mentions.map(id => ("@"+userStore.container[id]?.getFullName) || "@invalid reference");
      let   out: string = text;

      // Sort the mention ids according to occurance
      const _mentions = Comment.sortMention(mentions, text);

      // Replace the mention strings with the mention filler
      mentionStrings.forEach(m => {
        out = out.replace(new RegExp(m, "g"), Comment.mentionFiller);
      });

      await Comment.create({
        item_id:      commentStore.getActiveItem.id,
        room_id:      commentStore.getActiveItem.room_id,
        meeting_id:   meetingStore.getActiveMeeting?.id,
        private:      isPrivate,
        reply_id:     !!reply ? reply.id: null,
        text:         out,
        user_id:      currentUser.id,
        workspace_id: commentStore.getActiveItem.workspace_id,
        data_type:    commentStore.getActiveItem.data_type as ItemType,
        mentions:     _mentions,
      }).save();
      setIsPrivate(false);
      if (isPrivate) setViewPrivate(true);
      setText("");
      textAreaRef.current.setContent("");
      setReply(null);
    };

    const totalPrivates = useMemo(
      () => commentStore.getSortedComments.filter((x) => x.private).length,
      [commentStore.getSortedComments]
    );

    const commentList = React.useMemo(() => {
      return (
        <CommentsList>
          {commentStore.getSortedComments.map((c: Comment) => (
            <CommentEntry key={c.id} comment={c} hidePrivate={!viewPrivate} setReply={setReplyComment}/>
          ))}
        </CommentsList>
      )
    }, [commentStore.getSortedComments, viewPrivate]);

    return (
      <Grid>
        <Row flexDirection="column" gap="1rem" style={{margin: '2rem 0rem'}}>
          {!!reply && (
            <>
              <ReplyContainer>
                <Text>{`${t("reply_to")} ${
                  userStore.getItemByID(reply.user_id).getFullName
                }:`}</Text>
                <CommentContainer>{reply.text}</CommentContainer>
                <StyledActionIcon
                  size    = "xsmall"
                  cursor  = "pointer"
                  iconId  = "close"
                  onClick = {() => setReply(undefined)}
                  label   = {t("cancel")}
                />
              </ReplyContainer>
            </>
          )}
          <TextArea
            ref            = {textAreaRef}
            defaultValue   = {text}
            onChange       = {(text) => setText(text)}
            rows           = {5}
            // maxLength      = {5000}
            label          = {t("add_comment")}
            mentionOptions = {userStore.getMentions(commentStore.getActiveItem.room_id)}
            onMention      = {(user: User) => setMentions(mentions.concat(user.id))}
          />
        </Row>

        {/* Submit & select private  */}
        <Row justifyContent="space-between" flexDirection="row" marginTop='1rem'>
          <Checkbox
            label    = {t("private_comment")}
            checked  = {isPrivate}
            disabled = {false}
            onClick  = {() => setIsPrivate(!isPrivate)}
          />
          <Button
            size     = "small"
            disabled = {text.length < 1}
            variant  = "primary"
            onClick  = {createComment}
          >
            {t("save_comment")}
          </Button>
        </Row>

        <Spacer size="small"/>

        <Row marginBottom="1rem" flexDirection="column" alignItems="left" justifyContent="flex-start">
          {totalPrivates > 0 && (
            <Icon
              size    = "xsmall"
              cursor  = "pointer"
              iconId  = {viewPrivate ? "eye" : "eyeClosed"}
              onClick = {() => setViewPrivate(!viewPrivate)}
              label   = {`${
                viewPrivate ? t("hide_private_notes") : t("view_private_notes")
              } (${totalPrivates}) st`}
            />
          )}
        {commentList}
        </Row>
      </Grid>
    );
  })
);


const Box = styled.div<any>``;

const Grid = styled(Box)`
  grid-gap: 0.25rem 0rem;
  padding:  0.5rem;
`;

const Row = styled(Box)`
  display:         flex;
  flex-direction:  ${(props) => props.flexDirection || "row"};
  justify-content: ${(props) => props.justifyContent || "space-between"};
`;

const StyledActionIcon = styled(Icon)`
  margin-right: 2rem;
`;

const CommentContainer = styled.div`
  border-radius:  0.25rem;
  box-shadow:     0px 2px 1px -1px rgb(0 0 0 / 20%),
                  0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%);
  padding:     1rem;
  margin:      0rem;
  position:    relative;
  font-size:   ${theme.font.size.small};
  white-space: pre-line;
  span {
    font-weight: 300;
    font-size:   10px;
    color:       ${theme.color.primary8};
    position:    relative;
  }
`;

const CommentsList = styled(Box)`
  display:        flex;
  flex-direction: column;
  grid-gap:       0.5rem;
  overflow:       auto;
  margin-top: 2rem;
`;

const CommentActionsDelete = styled(Box)`
  display:     none;
  align-items: center;
  opacity:     0.5;
  margin-left: 0.5rem;
  &:hover {
    opacity:    1;
    transition: opacity 0.3s ease-in 1s;
  }
`;

const ReplyContainer = styled(Box)`
  display:        flex;
  flex-direction: column;
  gap:            0.5rem;
  padding:        0.25rem;
  margin-bottom:  1rem;
  ${CommentContainer} {
    box-shadow: none;
    background: #eee;
  }
  ${StyledActionIcon} {
    align-self: flex-end;
  }
  p {
    font-weight: 600;
  }
  &:hover {
    ${CommentActionsDelete} {
      display: flex;
    }
  }
`;